import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Calendar, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { MaintenanceScheduleDto, ScheduleType, FrequencyUnit } from '../types';
import DataGrid from '../components/grid/DataGrid';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import { formatDate } from '../utils/formatters';

const MaintenanceSchedulePage: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [sortField, setSortField] = useState<string>('nextDueDate');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceScheduleDto | null>(null);
	const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'overdue' | 'inactive'>('all');
	const [clientFilter, setClientFilter] = useState<number | 'all'>('all');
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// Fetch maintenance schedules
	const {
		data: schedulesResponse,
		isLoading,
		error,
		refetch
	} = useQuery({
		queryKey: ['maintenance-schedules', activeFilter],
		queryFn: () => apiService.getMaintenanceSchedules(),
	});

	// Fetch clients for the filter
	const { data: clientsData } = useQuery({
		queryKey: ['clients-for-schedule-filter'],
		queryFn: async () => {
			const clientsResponse = await apiService.getClients(1, 1000);
			return clientsResponse.data?.data || [];
		},
	});

	const clients = clientsData || [];

	// Delete schedule mutation
	const deleteScheduleMutation = useMutation({
		mutationFn: (id: number) => apiService.deleteMaintenanceSchedule(id),
		onSuccess: (response) => {
			if (response.success) {
				queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
				setIsDeleteModalOpen(false);
				setSelectedSchedule(null);
				toast.success('Maintenance schedule deleted successfully!');
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error deleting schedule:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error deleting schedule: ${errorMessage}`);
		}
	});

	const handleDelete = (schedule: MaintenanceScheduleDto) => {
		setSelectedSchedule(schedule);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = () => {
		if (selectedSchedule) {
			deleteScheduleMutation.mutate(selectedSchedule.id);
		}
	};

	const handleEdit = (schedule: MaintenanceScheduleDto) => {
		navigate(`/maintenance-schedules/${schedule.id}/edit`);
	};

	const handleAdd = () => {
		navigate('/maintenance-schedules/create');
	};

	const handleViewEquipment = (schedule: MaintenanceScheduleDto) => {
		navigate(`/equipment/${schedule.equipmentId}`);
	};

	// Get unique clients from schedules for filtering
	const availableClients = useMemo(() => {
		if (!schedulesResponse?.data || !clients.length) return [];
		const clientNames = new Set<string>();
		schedulesResponse.data.forEach(s => {
			if (s.clientName) {
				clientNames.add(s.clientName);
			}
		});
		// Return clients that have schedules, sorted by name
		return clients
			.filter(c => clientNames.has(c.name))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [schedulesResponse?.data, clients]);

	// Filter schedules
	const filteredSchedules = useMemo(() => {
		if (!schedulesResponse?.data) return [];
		
		let filtered = schedulesResponse.data;
		
		// Apply client filter
		if (clientFilter !== 'all') {
			const selectedClient = clients.find(c => c.id === clientFilter);
			if (selectedClient) {
				filtered = filtered.filter(s => s.clientName === selectedClient.name);
			}
		}
		
		// Apply active filter
		if (activeFilter === 'active') {
			filtered = filtered.filter(s => s.isActive);
		} else if (activeFilter === 'overdue') {
			filtered = filtered.filter(s => s.isActive && s.isOverdue);
		} else if (activeFilter === 'inactive') {
			filtered = filtered.filter(s => !s.isActive);
		}
		
		// Apply search filter
		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			filtered = filtered.filter(s =>
				s.name.toLowerCase().includes(term) ||
				s.equipmentName.toLowerCase().includes(term) ||
				s.clientLocationName.toLowerCase().includes(term) ||
				s.clientName.toLowerCase().includes(term)
			);
		}
		
		// Apply sorting
		filtered.sort((a, b) => {
			let aVal: any = a[sortField as keyof MaintenanceScheduleDto];
			let bVal: any = b[sortField as keyof MaintenanceScheduleDto];
			
			if (sortField === 'nextDueDate') {
				aVal = new Date(aVal).getTime();
				bVal = new Date(bVal).getTime();
			}
			
			if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
			if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
			return 0;
		});
		
		return filtered;
	}, [schedulesResponse?.data, clientFilter, clients, activeFilter, searchTerm, sortField, sortDirection]);

	const getStatusBadge = (schedule: MaintenanceScheduleDto) => {
		if (!schedule.isActive) {
			return (
				<span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
					Inactive
				</span>
			);
		}
		if (schedule.isOverdue) {
			return (
				<span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
					Overdue
				</span>
			);
		}
		if (schedule.daysUntilDue <= 7) {
			return (
				<span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
					Due Soon
				</span>
			);
		}
		return (
			<span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
				Scheduled
			</span>
		);
	};

	const columns = useMemo(() => [
		{
			id: 'name',
			header: 'Schedule Name',
			sortable: true,
			cell: (value: string, schedule: MaintenanceScheduleDto) => (
				<div className="flex flex-col">
					<span className="font-medium text-gray-900 dark:text-gray-100">{schedule.name}</span>
					<span className="text-sm text-gray-500 dark:text-gray-400">{schedule.frequencyDisplay}</span>
				</div>
			),
		},
		{
			id: 'equipmentName',
			header: 'Equipment',
			sortable: true,
			cell: (value: string, schedule: MaintenanceScheduleDto) => (
				<div className="flex flex-col">
					<button
						onClick={(e) => {
							e.stopPropagation();
							handleViewEquipment(schedule);
						}}
						className="text-left font-medium text-accent-color hover:underline"
					>
						{schedule.equipmentName}
					</button>
					<span className="text-sm text-gray-500 dark:text-gray-400">{schedule.equipmentTypeDisplay}</span>
				</div>
			),
		},
		{
			id: 'clientLocationName',
			header: 'Location',
			sortable: true,
			cell: (value: string, schedule: MaintenanceScheduleDto) => (
				<div className="flex flex-col">
					<span className="text-gray-900 dark:text-gray-100">{schedule.clientLocationName}</span>
					<span className="text-sm text-gray-500 dark:text-gray-400">{schedule.clientName}</span>
				</div>
			),
		},
		{
			id: 'nextDueDate',
			header: 'Next Due',
			sortable: true,
			cell: (value: string, schedule: MaintenanceScheduleDto) => (
				<div className="flex flex-col">
					<span className="text-gray-900 dark:text-gray-100">{formatDate(schedule.nextDueDate)}</span>
					<span className={`text-sm ${schedule.isOverdue ? 'text-red-600 dark:text-red-400' : schedule.daysUntilDue <= 7 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`}>
						{schedule.isOverdue ? `${Math.abs(schedule.daysUntilDue)} days overdue` : `${schedule.daysUntilDue} days`}
					</span>
				</div>
			),
		},
		{
			id: 'status',
			header: 'Status',
			cell: (value: any, schedule: MaintenanceScheduleDto) => getStatusBadge(schedule),
		},
		{
			id: 'autoGenerateWorkOrders',
			header: 'Auto-Generate',
			cell: (value: boolean, schedule: MaintenanceScheduleDto) => (
				<span className="text-gray-900 dark:text-gray-100">
					{schedule.autoGenerateWorkOrders ? (
						<CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
					) : (
						<XCircle className="h-5 w-5 text-gray-400" />
					)}
				</span>
			),
		},
		{
			id: 'actions',
			header: '',
			sticky: 'right' as const,
			stickyWidth: '120px',
			cell: (value: any, schedule: MaintenanceScheduleDto) => (
				<div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
					<button
						onClick={() => handleEdit(schedule)}
						className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
						title="Edit Schedule"
					>
						<Edit className="h-4 w-4" />
					</button>
					<button
						onClick={() => handleDelete(schedule)}
						className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
						title="Delete Schedule"
					>
						<Trash2 className="h-4 w-4" />
					</button>
				</div>
			),
		},
	], [handleEdit, handleDelete, handleViewEquipment]);

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading maintenance schedules</div>
					<div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex justify-between items-center">
				<div>
					<div className="flex items-center space-x-2">
						<Calendar className="h-6 w-6 text-gray-900 dark:text-gray-100" />
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Preventative Maintenance Schedules</h1>
					</div>
					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage and track preventative maintenance schedules</p>
				</div>
				<button
					onClick={handleAdd}
					className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
				>
					<Plus className="w-5 h-5" />
					Add Schedule
				</button>
			</div>

			{/* Filters */}
			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<div className="flex flex-col gap-4">
						{/* Client Filter */}
						<div className="flex items-center gap-4">
							<label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
								Client:
							</label>
							<select
								value={clientFilter === 'all' ? 'all' : clientFilter}
								onChange={(e) => {
									const newClientFilter = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
									setClientFilter(newClientFilter);
								}}
								className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm min-w-[200px]"
							>
								<option value="all">All Clients</option>
								{availableClients.map((client) => (
									<option key={client.id} value={client.id}>
										{client.name}
									</option>
								))}
							</select>
						</div>
						{/* Status Filters */}
						<div className="flex items-center gap-4">
							<button
								onClick={() => setActiveFilter('all')}
								className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
									activeFilter === 'all'
										? 'bg-accent-color text-white'
										: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								All
							</button>
							<button
								onClick={() => setActiveFilter('active')}
								className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
									activeFilter === 'active'
										? 'bg-accent-color text-white'
										: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								Active
							</button>
							<button
								onClick={() => setActiveFilter('overdue')}
								className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
									activeFilter === 'overdue'
										? 'bg-accent-color text-white'
										: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								Overdue
							</button>
							<button
								onClick={() => setActiveFilter('inactive')}
								className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
									activeFilter === 'inactive'
										? 'bg-accent-color text-white'
										: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								Inactive
							</button>
						</div>
					</div>
				</div>

				{/* Data Grid */}
				<DataGrid
					data={filteredSchedules}
					columns={columns}
					searchTerm={searchTerm}
					onSearch={setSearchTerm}
					onSort={handleSort}
					sortField={sortField}
					sortDirection={sortDirection}
					searchPlaceholder="Search schedules by name, equipment, location..."
					emptyMessage="No maintenance schedules found"
					isLoading={isLoading}
					getRowClassName={() => 'group'}
				/>
			</div>

			{/* Delete Confirmation Modal */}
			<ConfirmDialog
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedSchedule(null);
				}}
				onConfirm={confirmDelete}
				title="Delete Maintenance Schedule"
				message={`Are you sure you want to delete "${selectedSchedule?.name}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
				isLoading={deleteScheduleMutation.isPending}
			/>
		</div>
	);
};

export default MaintenanceSchedulePage;






